import { addManagerToClub, removeManagerFromClub } from '@/api/clubs/clubApi';
import ConfirmRemoveManagerDialog from '@/components/clubs/ConfirmRemoveManagerDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { CLUB_STATUS } from '@/types/club.model';
import { handleGlobalError } from '@/utils/error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  Info,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const inviteManagerSchema = z.object({
  email: z
    .string()
    .min(1, 'El correo electrónico es requerido')
    .email('Ingresa un correo electrónico válido')
    .max(255, 'El correo electrónico es demasiado largo'),
});

interface Manager {
  email: string;
  username: string;
  name: string;
}

type InviteManagerFormData = z.infer<typeof inviteManagerSchema>;

const InviteManager: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId, clubName, clubStatus, clubManagers } = location.state || {};

  const isDisabled = clubStatus === CLUB_STATUS.DISABLED;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [managersToRemove, setManagersToRemove] = useState<string[]>([]);
  const [currentManagers, setCurrentManagers] = useState<Manager[]>(
    clubManagers || [],
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<InviteManagerFormData>({
    resolver: zodResolver(inviteManagerSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const emailValue = watch('email');

  const onSubmit = async (data: InviteManagerFormData) => {
    if (!clubId) {
      toast({
        title: 'Error',
        description: 'No se pudo identificar el club.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await addManagerToClub(clubId, data.email);

      toast({
        title: 'Invitación enviada',
        description: `Se ha invitado exitosamente a ${data.email} como manager del club.`,
        variant: 'default',
      });

      reset();

      setTimeout(() => {
        navigate('/clubs');
      }, 2000);
    } catch (error) {
      handleGlobalError(
        error,
        'Error al enviar invitación',
        'No se pudo enviar la invitación. Intentá nuevamente más tarde.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveManager = async (managerEmail: string) => {
    if (!clubId) {
      toast({
        title: 'Error',
        description: 'No se pudo identificar el club.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setManagersToRemove((prev) => [...prev, managerEmail]);
      await removeManagerFromClub(clubId, managerEmail);

      setCurrentManagers((prev) =>
        prev.filter((manager) => manager.email !== managerEmail),
      );

      toast({
        title: 'Manager eliminado',
        description: `Se ha eliminado exitosamente a ${managerEmail} de la gestión del club.`,
        variant: 'default',
      });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      toast({
        title: 'Error al eliminar manager',
        description:
          axiosError.response?.data?.message ||
          'No se pudo eliminar el manager.',
        variant: 'destructive',
      });
    } finally {
      setManagersToRemove((prev) =>
        prev.filter((email) => email !== managerEmail),
      );
    }
  };

  const openRemoveDialog = (manager: Manager) => {
    setSelectedManager(manager);
    setDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    if (selectedManager) {
      handleRemoveManager(selectedManager.email);
    }
  };

  const handleBack = () => {
    navigate('/clubs');
  };

  if (!clubId || !clubName) {
    navigate('/clubs');
    return null;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 p-2">
        <Button variant="ghost" onClick={handleBack} className="left-4 top-4">
          <ChevronLeft size={16} />
        </Button>
        <h2 className="text-2xl font-bold">
          Administrar Managers:{' '}
          <span className="text-blue-600">{clubName}</span>
        </h2>
      </div>
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="grid gap-8">
          <Card className="shadow-lg">
            <CardHeader className="relative text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <UserPlus size={32} className="text-blue-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Invitar Manager de Torneos
              </CardTitle>
              <p className="mt-2 text-gray-600">
                Club:{' '}
                <span className="font-semibold text-gray-900">{clubName}</span>
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {isDisabled && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      size={20}
                      className="mt-0.5 flex-shrink-0 text-red-600"
                    />
                    <div>
                      <h4 className="mb-1 font-semibold text-red-900">
                        Club Deshabilitado
                      </h4>
                      <p className="text-sm text-red-800">
                        No puedes invitar managers porque el club está
                        actualmente deshabilitado. Para poder invitar managers,
                        primero debes{' '}
                        <Link
                          to="/clubs"
                          className="font-semibold underline hover:text-red-900"
                        >
                          habilitar el club desde la lista de clubes
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <Info
                    size={20}
                    className="mt-0.5 flex-shrink-0 text-blue-600"
                  />
                  <div>
                    <h3 className="mb-2 font-semibold text-blue-900">
                      ¿Qué puede hacer un Manager de Torneos?
                    </h3>
                    <p className="text-sm leading-relaxed text-blue-800">
                      El usuario que invites como manager tendrá acceso para{' '}
                      <strong>crear y gestionar torneos</strong> en tu club.
                      Podrá usar las instalaciones y canchas disponibles para
                      organizar competencias deportivas.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-700"
                  >
                    Correo electrónico del manager
                  </Label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                    />
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="ejemplo@correo.com"
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      disabled={isDisabled}
                    />
                    {emailValue && !errors.email && (
                      <CheckCircle
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transform text-green-500"
                      />
                    )}
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || isSubmitting || isDisabled}
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      Enviando invitación...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} className="mr-2" />
                      Invitar como Manager
                    </>
                  )}
                </Button>
              </form>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <Info
                    size={20}
                    className="mt-0.5 flex-shrink-0 text-amber-600"
                  />
                  <div>
                    <h4 className="mb-1 font-semibold text-amber-900">
                      Importante
                    </h4>
                    <p className="text-sm text-amber-800">
                      Si la persona todavía no tiene cuenta, le llega un email
                      para registrarse. Cuando cree su cuenta{' '}
                      <strong>con ese mismo email</strong>, queda agregada como
                      manager del club.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <Users size={32} className="text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Managers Actuales
              </CardTitle>
              <p className="mt-2 text-gray-600">
                {currentManagers.length}{' '}
                {currentManagers.length === 1 ? 'manager' : 'managers'} en el
                club
              </p>
            </CardHeader>

            <CardContent>
              {currentManagers.length > 0 ? (
                <div className="space-y-3">
                  {currentManagers.map((manager) => (
                    <div
                      key={manager.email}
                      className="flex items-center justify-between rounded-lg border bg-gray-50 p-4"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {manager.name || manager.username}
                        </h4>
                        <p className="text-sm text-gray-600">{manager.email}</p>
                        {manager.name && manager.username && (
                          <p className="text-xs text-gray-500">
                            {manager.username}
                          </p>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        disabled={managersToRemove.includes(manager.email)}
                        onClick={() => openRemoveDialog(manager)}
                      >
                        {managersToRemove.includes(manager.email) ? (
                          <>
                            <div className="mr-2 h-3 w-3 animate-spin rounded-full border-b-2 border-red-600"></div>
                            Eliminando...
                          </>
                        ) : (
                          <>
                            <Trash2 size={14} className="mr-2" />
                            Eliminar
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <Users size={24} className="text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">
                    No hay managers asignados a este club todavía.
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Invita a alguien para que te ayude a gestionar los torneos.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <ConfirmRemoveManagerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          manager={selectedManager}
          onConfirm={handleConfirmRemove}
          isLoading={
            selectedManager
              ? managersToRemove.includes(selectedManager.email)
              : false
          }
        />
      </div>
    </div>
  );
};

export default InviteManager;
